import { useState } from 'react';
import { UserService } from '@/api/services/userService';
import {
  UserUpdateParams,
  UserInfo,
} from '@/api/types';
import { useTabAuth } from '../useTabAuth';
import { logPrint } from '@/utils/my-utils';

/**
 * 用户资料管理相关的 hook
 * 处理用户信息更新、获取、搜索等操作
 */
export function useUserProfile() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const tabAuth = useTabAuth();

  /**
   * 执行用户操作的通用方法
   * @param operation 操作函数
   * @param params 操作参数
   * @param successMessage 成功提示信息
   * @returns Promise<T | null> 操作结果
   */
  const executeOperation = async <T, P>(
    operation: (params?: P) => Promise<T>,
    params: P | undefined,
    successMessage: string
  ): Promise<T | null> => {
    setIsLoading(true);
    setError('');

    try {
      const result = await operation(params);
      logPrint(successMessage);
      return result;
    } catch (err: any) {
      console.error('用户操作失败:', err);
      const errorMessage = err?.err_msg || '操作失败，请重试';
      setError(errorMessage);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 更新用户信息
   * @param params 更新参数
   * @returns Promise<UserInfo | null> 更新后的用户信息
   */
  const updateUserInfo = async (params: UserUpdateParams): Promise<UserInfo | null> => {
    setIsLoading(true);
    setError('');

    try {
      const result = await UserService.updateUserInfo(params);
      
      let localUserInfo = tabAuth.getUserInfo();
      localUserInfo = {
        ...localUserInfo,
        ...result
      };
      tabAuth.setUserInfo(localUserInfo);
      
      return result;
    } catch (err: any) {
      console.error('用户操作失败:', err);
      const errorMessage = err?.err_msg || '操作失败，请重试';
      setError(errorMessage);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 获取用户信息
   * @param q 搜索查询（用户ID、handle或昵称，可选）
   * @returns Promise<UserInfo | null> 用户信息
   */
  const getUserInfo = async (q?: string): Promise<UserInfo | null> => {
    return executeOperation(
      UserService.getUserInfo,
      q,
      '用户信息获取成功'
    );
  };

  /**
   * 搜索用户
   * @param params 搜索参数
   * @returns Promise<UserSearchResult | null> 搜索结果
   */
  const searchUsers = async (q?: string): Promise<UserInfo[] | null> => {
    setIsLoading(true);
    setError('');

    try {
      const result = await UserService.getUserInfo(q);
      if (result) {
        return [result];
      }
      return [];
    } catch (err: any) {
      console.error('用户操作失败:', err);
      if (err?.code === 100002) {
        setError('用户不存在');
      } else if (err?.err_msg) {
        setError(err.err_msg);
      } else {
        setError('操作失败，请重试');
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 获取当前用户信息（无参数版本）
   * @returns Promise<UserInfo | null> 当前用户信息
   */
  const getCurrentUserInfo = async (): Promise<UserInfo | null> => {
    return getUserInfo();
  };

  /**
   * 批量获取用户信息
   * @param userQueries 用户查询列表
   * @returns Promise<UserInfo[]> 用户信息列表
   */
  const batchGetUserInfo = async (userQueries: string[]): Promise<UserInfo[]> => {
    setIsLoading(true);
    setError('');

    try {
      const promises = userQueries.map(query => UserService.getUserInfo(query));
      const results = await Promise.allSettled(promises);
      
      const userInfos: UserInfo[] = [];
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          userInfos.push(result.value);
        } else {
          console.warn(`获取用户信息失败 (${userQueries[index]}):`, result.reason);
        }
      });

      logPrint(`批量获取用户信息完成，成功: ${userInfos.length}/${userQueries.length}`);
      return userInfos;
    } catch (err: any) {
      console.error('批量获取用户信息失败:', err);
      setError('批量获取用户信息失败');
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 清除错误信息
   */
  const clearError = () => {
    setError('');
  };

  return {
    // 状态
    isLoading,
    error,

    // 操作方法
    updateUserInfo,
    getUserInfo,
    searchUsers,
    getCurrentUserInfo,
    batchGetUserInfo,

    // 工具方法
    clearError
  };
}
